import React from 'react'
import { type TableProps, type DrawerProps } from 'antd'
import { ChevronLeftFilled } from '@fluentui/react-icons'
import {
  showMSAlert,
  showMSError,
  formatCapitalizedString,

  type IMSTableColumnType
} from '@choreograph/layout'

import { type IUserPermission, type IRTKQResponseResult } from '../../constants/types/common'
import { type IOktaUser } from '../types/generatedTypesAliases'
import { UserStatus } from '../../constants/types/common'
import { BooleanLabelIcon } from '../../components/shared'

export const NOTIFICATION_DURATION = 5

export const getAbbreviation = (...args: string[]): string =>
  args
    .map(el => el.charAt(0).toUpperCase())
    .join()
    .replace(',', '')

export const getNamespaceInfoLink = (namespace: string) => 'https://insidemedia.sharepoint.com' +
  '/:w:/r/sites/CHEngineeringGroup/Shared%20Documents/Client%20Engineering/' +
  'AuthenticationAndAuthorization/SecurityModelDescriptions/' +
  `${namespace}.docx?web=1`

export const getTablePaginationProp = <T,>(entity: string, itemNumber: number): Pick<TableProps<T>, 'pagination'> => {
  const capitalizedEntity = formatCapitalizedString(entity)

  return {
    ...(itemNumber >= 10)
      ? {
          pagination: {
            position: ['bottomCenter'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} ${entity}`,
            showSizeChanger: {
              prefix: `${capitalizedEntity} per page`,
              variant: 'borderless',
              showSearch: false,
              labelRender: ({ value }) => value
            }
          }
        }
      : { pagination: false }
  }
}

export const showNotification = ({ result, success, error }: { result: IRTKQResponseResult, success: string, error?: string }) => {
  if ('data' in result) {
    showMSAlert({
      duration: NOTIFICATION_DURATION,
      type: 'success',
      message: success
    })
  }

  if ('error' in result && !error) {
    if ('data' in result.error) {
      if (Array.isArray(result.error.data)) {
        const messages = result.error.data as string[]
        messages.forEach(message => {
          showMSError({ duration: NOTIFICATION_DURATION, message })
        })
      } else {
        showMSError({ duration: NOTIFICATION_DURATION, message: 'There has been an error. Please try again, or contact an administrator.' })
      }
    }
  }

  if (error) {
    showMSError({
      message: error
    })
  }
}

export const removeDuplicates = (arr) => {
  const seen = new Set()
  return arr.filter(item => {
    const serialized = JSON.stringify(item)
    if (seen.has(serialized)) return false
    seen.add(serialized)
    return true
  })
}

const colorMapPalette10 = {
  0: '#DDA800',
  1: '#92C024',
  2: '#00CD75',
  3: '#FF6A7B',
  4: '#FF8A0B',
  5: '#5EA8FF',
  6: '#FF75D5',
  7: '#B990FF',
  8: '#00CFE5',
  9: '#AFB5BA'
}

export const pageTitlePadding: string = '0.5rem'

export const getAvatarBackgroundColor = (abbr: string) => (
  colorMapPalette10[abbr.charCodeAt(0) % Object.keys(colorMapPalette10).length]
)

export const operationDrawerProps: Partial<DrawerProps> = {
  rootClassName: 'permission-manager',
  classNames: {
    header: 'drawer-header'
  },
  closeIcon: <ChevronLeftFilled />,
  closable: { 'aria-label': 'Close Button' },
  width: '60%'
}

export const transformRelationship = (relationship) => {
  const { resourceType: compoundResourceType, resourceId, relation, expiresAt } = relationship
  const [namespace, resourceType] = compoundResourceType?.split('/') || []
  return {
    namespace,
    resourceType,
    resourceId,
    relation,
    expiresAt: expiresAt ?? null
  }
}

export const setPermissionRowKey = (permission: IUserPermission): string => {
  const { resourceId, namespace, resourceType, relation, expiresAt } = permission
  return [resourceId, namespace, resourceType, relation, expiresAt ?? ''].join('%%%')
}

export const parsePermissionRowKey = (permission: string): IUserPermission => {
  const [resourceId, namespace, resourceType, relation, expiresAt] = permission.split('%%%')
  return { resourceId, namespace, resourceType, relation, expiresAt: expiresAt || null }
}

export const userStaticColumns: Array<IMSTableColumnType<IOktaUser>> = [
  {
    title: 'First Name',
    width: 145,
    dataIndex: ['firstName'],
    sorterProps: {
      dataType: 'text'
    }
  },
  {
    title: 'Last Name',
    width: 145,
    dataIndex: ['lastName'],
    sorterProps: {
      dataType: 'text'
    }
  },
  {
    title: 'Email',
    dataIndex: ['email'],
    sorterProps: {
      dataType: 'text'
    }
  },
  {
    title: 'User ID',
    dataIndex: 'id',
    sorterProps: {
      dataType: 'numerical'
    }
  },
  {
    title: 'Timezone',
    width: 130,
    dataIndex: 'timeZone',
    sorterProps: {
      dataType: 'text'
    }
  },
  {
    title: 'Active',
    width: 90,
    sorter: (a: IOktaUser, b) => UserStatus[a.status!].localeCompare(UserStatus[b.status!]),
    render: (v) => <BooleanLabelIcon showLabel={false} value={UserStatus.ACTIVE === v.status} />
  },
]
